import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Users, Ticket, Trophy, LogOut, List, Gift, Zap, Settings as SettingsIcon, Trash2, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import AccountSecurity from './AccountSecurity';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [testDataPreview, setTestDataPreview] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    loadStats();
    loadTestDataPreview();
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await api.get('/dashboard/stats');
      setStats(data);
    } catch (error) {
      toast.error('Failed to load statistics');
    }
  };

  const loadTestDataPreview = async () => {
    try {
      const { data } = await api.get('/admin/test-data/preview');
      setTestDataPreview(data);
    } catch (error) {
      console.error('Failed to load test data preview');
    }
  };

  const handleClearTestData = async () => {
    if (confirmPhrase !== 'CLEAR TEST DATA') {
      toast.error('You must type "CLEAR TEST DATA" exactly to confirm');
      return;
    }
    setClearing(true);
    try {
      const { data } = await api.post('/admin/test-data/clear', { confirmation: 'CLEAR TEST DATA' });
      toast.success(`Cleared ${data.entries_deleted} test entries and ${data.draws_deleted} draws`);
      setClearDialogOpen(false);
      setConfirmPhrase('');
      loadStats();
      loadTestDataPreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear test data');
    } finally {
      setClearing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Header */}
      <div className="bg-[#121212] border-b border-[#D4AF37]/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-[#A0A0A0]">Welcome, {user?.name}</span>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="btn-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
            {[
              { icon: Ticket, label: 'Total Entries', value: stats.total_entries, color: '#D4AF37' },
              { icon: Ticket, label: 'Active Entries', value: stats.active_entries, color: '#F3A6C8' },
              { icon: Users, label: 'Unique Participants', value: stats.unique_participants, color: '#D4AF37' },
              { icon: Trophy, label: 'Total Winners', value: stats.total_winners, color: '#F3A6C8' },
            ].map((stat, index) => (
              <Card key={index} className="bg-[#121212] border border-[#D4AF37]/10 p-6">
                <stat.icon className="w-8 h-8 mb-3" style={{ color: stat.color }} />
                <div className="text-3xl font-bold text-white mb-1">{stat.value.toLocaleString()}</div>
                <div className="text-sm text-[#A0A0A0]">{stat.label}</div>
              </Card>
            ))}
          </div>
        )}

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="action-cards">
          {[
            {
              to: '/admin/entries',
              icon: List,
              title: 'Entry Management',
              description: 'Add, edit, and manage entries',
              color: '#D4AF37',
              testId: 'card-entries'
            },
            {
              to: '/admin/prizes',
              icon: Gift,
              title: 'Prize Management',
              description: 'Manage prizes and quantities',
              color: '#F3A6C8',
              testId: 'card-prizes'
            },
            {
              to: '/admin/draw',
              icon: Zap,
              title: 'Draw Control',
              description: 'Run and manage live draws',
              color: '#D4AF37',
              testId: 'card-draw'
            },
            {
              to: '/admin/winners',
              icon: Trophy,
              title: 'Winner History',
              description: 'View all draw results',
              color: '#F3A6C8',
              testId: 'card-winners'
            },
            {
              to: '/admin/settings',
              icon: SettingsIcon,
              title: 'Event Settings',
              description: 'Configure event details',
              color: '#D4AF37',
              testId: 'card-settings'
            },
            {
              to: '/admin/content',
              icon: FileText,
              title: 'Content Management',
              description: 'Edit hero, footer, rules, how-to-join and more',
              color: '#F3A6C8',
              testId: 'card-content'
            },
            {
              to: '/live-draw',
              icon: Zap,
              title: 'Live Draw Page',
              description: 'Open full-screen draw display',
              color: '#F3A6C8',
              testId: 'card-live-draw'
            },
          ].map((card, index) => (
            <Link key={index} to={card.to} data-testid={card.testId}>
              <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40 hover:shadow-[0_4px_24px_rgba(212,175,55,0.15)] cursor-pointer">
                <card.icon className="w-10 h-10 mb-4" style={{ color: card.color }} />
                <h3 className="text-lg font-bold text-white mb-2">{card.title}</h3>
                <p className="text-sm text-[#A0A0A0]">{card.description}</p>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Entries */}
        {stats?.recent_entries && stats.recent_entries.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-white mb-4">Recent Entries</h2>
            <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6">
              <div className="space-y-3">
                {stats.recent_entries.map((entry, index) => (
                  <div key={entry.id} className="flex items-center justify-between py-3 border-b border-[#D4AF37]/10 last:border-0">
                    <div>
                      <div className="text-white font-semibold">{entry.sugo_id}</div>
                      <div className="text-[#A0A0A0] text-sm">{entry.username || 'No username'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#D4AF37] text-sm">{entry.status}</div>
                      <div className="text-[#A0A0A0] text-xs">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Account Security - Change Admin Password */}
        <AccountSecurity />

        {/* Danger Zone - Clear Test Data */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Danger Zone
          </h2>          <Card className="bg-[#121212] border border-red-500/30 p-6" data-testid="danger-zone-card">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-1">Clear Test Data</h3>
                <p className="text-sm text-[#A0A0A0] mb-2">
                  Permanently deletes only entries and draws marked as test data.
                  Production entries (is_test = false) and all prizes/settings are preserved.
                </p>
                {testDataPreview && (
                  <div className="text-sm space-y-1" data-testid="test-data-preview">
                    <div className="text-[#F3A6C8]">
                      Test entries: <span className="font-bold">{testDataPreview.test_entries_count}</span>
                    </div>
                    <div className="text-[#F3A6C8]">
                      Linked test draws: <span className="font-bold">{testDataPreview.test_draws_count}</span>
                    </div>
                    <div className="text-green-400">
                      Production entries (protected): <span className="font-bold">{testDataPreview.production_entries_count}</span>
                    </div>
                  </div>
                )}
              </div>
              <Button
                onClick={() => setClearDialogOpen(true)}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="btn-open-clear-test-data"
                disabled={!testDataPreview || testDataPreview.test_entries_count === 0}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Test Data
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Clear Test Data Confirmation Dialog */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent className="bg-[#121212] border border-red-500/40 text-white" data-testid="clear-test-data-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Clear Test Data — Are you sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0A0A0]">
              This will permanently delete <span className="text-red-400 font-bold">{testDataPreview?.test_entries_count || 0} test entries</span> and{' '}
              <span className="text-red-400 font-bold">{testDataPreview?.test_draws_count || 0} linked draws</span>.
              Production data is not affected. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="confirm-phrase" className="text-white text-sm">
              To confirm, type <code className="bg-[#080808] px-2 py-1 rounded text-red-400">CLEAR TEST DATA</code>
            </Label>
            <Input
              id="confirm-phrase"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              className="mt-2 bg-[#080808] border-red-500/30 text-white"
              placeholder="Type here to confirm"
              data-testid="input-confirm-clear"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setConfirmPhrase('')}
              className="bg-[#080808] border-[#A0A0A0]/30 text-white hover:bg-[#121212]"
              data-testid="btn-cancel-clear"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearTestData}
              disabled={confirmPhrase !== 'CLEAR TEST DATA' || clearing}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="btn-confirm-clear-test-data"
            >
              {clearing ? 'Clearing...' : 'Delete Test Data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;